import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';

import leapifyLogo from 'assets/images/Leapify-Logo.svg';
import { type FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { cn } from 'utils/helpers';

import ChangePasswordForm from '../../../public/forms/ChangePasswordForm';
import {
  CHANGE_PASSWORD_FORM,
  CHANGE_PASSWORD_SCHEMA,
} from '../../../public/forms/schema';
import { useChangePassword, useVerifyOtp } from '../../../public/hooks';

const FORM_INIT = {
  password: '',
  passwordConfirmation: '',
};

interface PROPS {
  changedPassword: Dispatch<SetStateAction<boolean>>;
}

const Form: FC<PROPS> = ({ changedPassword }) => {
  const [params] = useSearchParams();
  const { isLoading: otpLoading, mutate: verifyOtp } = useVerifyOtp();
  const {
    data: result,
    isLoading,
    mutate: changePassword,
  } = useChangePassword();

  const otp = useMemo(() => params.get('otp') ?? '', [params]);
  const referenceId = useMemo(() => params.get('referenceId') ?? '', [params]);

  const onSubmit = (
    values: CHANGE_PASSWORD_FORM,
    { setSubmitting }: FormikHelpers<CHANGE_PASSWORD_FORM>,
  ) => {
    changePassword(
      {
        ...values,
        otp,
        referenceId,
        type: 'FORGOT_PASSWORD',
      },
      {
        onSuccess: () => {
          changedPassword(true);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  useEffect(() => {
    verifyOtp(
      {
        otp,
        referenceId,
        type: 'FORGOT_PASSWORD',
        source: 'account',
      },
      {
        onError: () => {
          window.location.href = '/';
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, referenceId]);

  useEffect(() => {
    if (result) changedPassword(true);
  }, [result, changedPassword]);

  return (
    <div
      data-aos="fade-up"
      className={cn(
        'bg-fill-tertiary rounded-[6px] shadow-leapify-sm',
        'px-[31px]',
        'py-[56px]',
        'md:w-[600px]',
        'sm:w-full',
        'text-center',
      )}
    >
      <img
        alt="Leapify Logo"
        className="w-[170px] mb-[55px] inline-block mx-auto"
        src={leapifyLogo}
      />
      <div className="max-w-[428px] mx-auto">
        <h3 className={cn('text-center', 'text-xl font-semibold', 'mb-5')}>
          New Password
        </h3>
        <Formik
          initialValues={FORM_INIT as CHANGE_PASSWORD_FORM}
          onSubmit={onSubmit}
          validationSchema={CHANGE_PASSWORD_SCHEMA}
        >
          <ChangePasswordForm disabled={isLoading && otpLoading} />
        </Formik>
      </div>
    </div>
  );
};

export default Form;
